import { useRef, useCallback } from 'react'
import { haptic } from '@/lib/haptics'

interface UseLongPressOptions {
  delay?: number
  onLongPress: () => void
  disabled?: boolean
}

export function useLongPress({
  delay = 300,
  onLongPress,
  disabled = false,
}: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggeredRef = useRef(false)
  const startPos = useRef({ x: 0, y: 0 })

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return
      triggeredRef.current = false
      startPos.current = { x: e.clientX, y: e.clientY }
      timerRef.current = setTimeout(() => {
        triggeredRef.current = true
        haptic('light')
        onLongPress()
      }, delay)
    },
    [disabled, delay, onLongPress]
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!timerRef.current) return
      const dx = e.clientX - startPos.current.x
      const dy = e.clientY - startPos.current.y
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        clear()
      }
    },
    [clear]
  )

  const onPointerUp = useCallback(() => {
    clear()
  }, [clear])

  const onContextMenu = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault()
  }, [])

  return {
    triggered: triggeredRef,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerLeave: onPointerUp,
      onContextMenu,
      'data-long-press': true as const,
    },
  }
}
